export type UniformValue = number | boolean | Float32Array | Int32Array | number[];

interface UniformInfo {
  location: WebGLUniformLocation;
  type: number;
}

function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
  name: string,
): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown error';
    gl.deleteShader(shader);
    const kind = type === gl.VERTEX_SHADER ? 'vertex' : 'fragment';
    throw new Error(`Failed to compile the ${kind} shader "${name}":\n${log}`);
  }
  return shader;
}

export class Program {
  readonly program: WebGLProgram;
  private readonly gl: WebGL2RenderingContext;
  private readonly uniforms = new Map<string, UniformInfo>();

  constructor(gl: WebGL2RenderingContext, vertexSource: string, fragmentSource: string, name = '') {
    this.gl = gl;
    const vert = compileShader(gl, gl.VERTEX_SHADER, vertexSource, name);
    const frag = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, name);

    const program = gl.createProgram()!;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    gl.deleteShader(vert);
    gl.deleteShader(frag);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) ?? 'unknown error';
      gl.deleteProgram(program);
      throw new Error(`Failed to link the program "${name}":\n${log}`);
    }

    this.program = program;

    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < count; i += 1) {
      const info = gl.getActiveUniform(program, i);
      if (!info) continue;
      // Array uniforms report as `name[0]`; store both spellings.
      const base = info.name.replace(/\[0\]$/, '');
      const location = gl.getUniformLocation(program, info.name);
      if (location) this.uniforms.set(base, { location, type: info.type });
    }
  }

  use(): void {
    this.gl.useProgram(this.program);
  }

  has(name: string): boolean {
    return this.uniforms.has(name);
  }

  setUniforms(values: Record<string, UniformValue | undefined>): void {
    for (const [name, value] of Object.entries(values)) {
      if (value === undefined) continue;
      this.setUniform(name, value);
    }
  }

  setUniform(name: string, value: UniformValue): void {
    const info = this.uniforms.get(name);
    if (!info) return; // Uniform optimised out by the driver — silently ignore.
    const { gl } = this;
    const { location, type } = info;

    switch (type) {
      case gl.FLOAT:
        gl.uniform1f(location, value as number);
        return;
      case gl.FLOAT_VEC2:
        gl.uniform2fv(location, value as Float32Array);
        return;
      case gl.FLOAT_VEC3:
        gl.uniform3fv(location, value as Float32Array);
        return;
      case gl.FLOAT_VEC4:
        gl.uniform4fv(location, value as Float32Array);
        return;
      case gl.INT:
      case gl.BOOL:
      case gl.SAMPLER_2D:
      case gl.INT_SAMPLER_2D:
      case gl.UNSIGNED_INT_SAMPLER_2D:
        gl.uniform1i(location, typeof value === 'boolean' ? (value ? 1 : 0) : (value as number));
        return;
      case gl.INT_VEC2:
      case gl.BOOL_VEC2:
        gl.uniform2iv(location, value as Int32Array);
        return;
      case gl.INT_VEC3:
      case gl.BOOL_VEC3:
        gl.uniform3iv(location, value as Int32Array);
        return;
      case gl.INT_VEC4:
      case gl.BOOL_VEC4:
        gl.uniform4iv(location, value as Int32Array);
        return;
      case gl.FLOAT_MAT3:
        gl.uniformMatrix3fv(location, false, value as Float32Array);
        return;
      case gl.FLOAT_MAT4:
        gl.uniformMatrix4fv(location, false, value as Float32Array);
        return;
      default:
        throw new Error(`Uniform "${name}" has an unsupported type 0x${type.toString(16)}`);
    }
  }

  dispose(): void {
    this.gl.deleteProgram(this.program);
    this.uniforms.clear();
  }
}

/** Compiles each unique shader pair once per context. */
export class ProgramCache {
  private readonly cache = new Map<string, Program>();

  constructor(private readonly gl: WebGL2RenderingContext) {}

  get(name: string, vertexSource: string, fragmentSource: string): Program {
    const existing = this.cache.get(name);
    if (existing) return existing;
    const program = new Program(this.gl, vertexSource, fragmentSource, name);
    this.cache.set(name, program);
    return program;
  }

  dispose(): void {
    for (const program of this.cache.values()) program.dispose();
    this.cache.clear();
  }
}
